<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import { ref, watch } from "vue";

import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { useGetSiteDetailsQuery } from "@/composables/kili/sites/queries/use-get-site-details-query";
import { useBrandedNumberRouteParams } from "@/composables/use-route-params/use-hospitality-number-route-param";
import { EConsumptionPerimeterType, EConsumptionQueryAction } from "@/data/api/kili/consumption/consumption-query-keys";
import { TSiteSection, siteSectionsDataTable } from "@/data/api/kili/sites/types/site-sections.type";
import { V4HSiteId } from "@/data/common/ids";
import { useTypedIntl } from "@/locales/typed-intl";
import router from "@/router/router";
import { ERouteName } from "@/router/router.type";
import { newSectionManagementFeatureFlag } from "@/services/feature-flags/env-feature-flags";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";
import { hasAtLeastKeyPart } from "@/utils/libs/tanstack-utils";

import Column from "primevue/column";

import SectionAppliancesDialog from "@/app/tools/section-management/site-sections/section-appliances-dialog.vue";
import BaseLayout from "@/components/layout/base-layout.vue";
import SiteDetailsCard from "@/components/tools/site-details/site-details-card.vue";
import KiliDataTableReader from "@/components/ui/paginated-data-table/kili-data-table-reader.vue";

const { brandedNumberRouteParam: siteId } = useBrandedNumberRouteParams<V4HSiteId>("siteId");

const { t, k } = useTypedIntl();
const queryClient = useQueryClient();
const { isEnvFeatureEnabled } = useFeatureFlag();

const isSectionAppliancesDialogVisible = ref<boolean>(false);
const viewedSection = ref<TSiteSection>();

const { data: siteDetails, isLoading: isLoadingSiteDetails } = useGetSiteDetailsQuery(siteId);

function handleViewSectionAppliances(section: TSiteSection) {
  viewedSection.value = section;
  isSectionAppliancesDialogVisible.value = true;
}

async function handleEditRow() {
  MixpanelService.trackEvent(EMixpanelEvent.SECTION_UPDATED);

  await queryClient.removeQueries({
    predicate: hasAtLeastKeyPart({ action: EConsumptionQueryAction.FIND_DSO_CONSUMPTION }),
    type: "all",
  });

  await queryClient.removeQueries({
    predicate: hasAtLeastKeyPart({
      action: EConsumptionQueryAction.FIND_MISSING_INFOS,
      perimeter: EConsumptionPerimeterType.MONOSITE,
    }),
    type: "all",
  });
}

watch(
  siteId,
  (newSiteId) => {
    if (newSiteId < 0) {
      if (isEnvFeatureEnabled(newSectionManagementFeatureFlag)) {
        router.push({ name: ERouteName.SECTION_MANAGEMENT_NEW });
        return;
      }
      router.push({ name: ERouteName.SECTION_MANAGEMENT });
    }
  },
  { immediate: true },
);
</script>

<template>
  <BaseLayout>
    <SiteDetailsCard
      :is-loading="isLoadingSiteDetails"
      :site-details="siteDetails"
      :site-id="siteId"
    />

    <div class="w-full h-full pb-8">
      <div class="relative w-full h-full">
        <div class="absolute inset-0">
          <KiliDataTableReader
            ref="table"
            :datatable="siteSectionsDataTable"
            edit-mode="row"
            :search-params="{ siteId }"
            :search-query-key-id="siteId"
            @row-edit-init="MixpanelService.trackEvent(EMixpanelEvent.EDIT_SECTION)"
            @row-edited="handleEditRow()"
          >
            <template #additional-columns>
              <Column>
                <template #body="{ data }">
                  <span
                    v-if="data.modulatorCount > 0"
                    aria-controls="section-appliances-dialog"
                    :aria-expanded="!!viewedSection"
                    aria-haspopup="dialog"
                    aria-label="show-section-appliances-button"
                    class="cursor-pointer font-semibold"
                    data-test-id="show-section-appliances-button"
                    role="button"
                    tabindex="0"
                    @click="handleViewSectionAppliances(data)"
                    @keydown.enter="handleViewSectionAppliances(data)"
                  >
                    {{ t(k.tools.siteManagement.siteSections.table.column.viewAppliances) }}
                  </span>
                </template>
              </Column>
            </template>
          </KiliDataTableReader>
        </div>
      </div>
    </div>
  </BaseLayout>

  <SectionAppliancesDialog
    v-if="viewedSection"
    id="section-appliances-dialog"
    v-model="isSectionAppliancesDialogVisible"
    :section="viewedSection"
    :site-id="siteId"
  />
</template>
